/* eslint-disable react-hooks/set-state-in-effect, react-hooks/immutability --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: standard "fetch / sync on dependency change" useEffects
 * with setState in async IIFEs. eslint-plugin-react-hooks v5's
 * set-state-in-effect rule is over-eager for these patterns. Other
 * React-19 lints (rules-of-hooks, exhaustive-deps) remain enabled.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ReceiptStatusBadge, deriveReceiptStatus } from "@/components/ReceiptStatusBadge";
import { api, API, getToken } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";
import { recordThirdPartyView } from "@/lib/thirdPartyView";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { ChildScopeBadge } from "@/components/ChildScopeBadge";
import { UpcomingExpensesSection } from "@/components/expenses/UpcomingExpensesSection";
import { Button } from "@/components/ui/button";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Wallet as WalletIcon, Paperclip, Download, Pencil, XCircle, ChevronDown, ChevronUp, ChevronRight, Trash2, ArrowRight, Check, Ban, Clock, CheckCircle2, HandCoins, CalendarClock, Receipt, SlidersHorizontal } from "lucide-react";
import { PaymentCreditsPanel, PeriodChipBar, PERIOD_PRESETS, computeRange } from "@/components/expenses/PaymentCreditsPanel";
import { InfoTooltip } from "@/components/InfoTooltip";
import { ParentBadge } from "@/components/ParentBadge";
import { getParentColorById, tintBg } from "@/lib/parentColor";
import { formatDate, money, currencySymbol } from "@/lib/format";
import { ChildAssignPicker } from "@/components/expenses/ChildAssignPicker";
import { useExpensesFilters } from "@/components/expenses/useExpensesFilters";
import { SummaryCards } from "@/components/expenses/SummaryCards";
import { PerChildSummary } from "@/components/expenses/PerChildSummary";
import { ScopeBreadcrumb } from "@/components/expenses/ScopeBreadcrumb";
import { ExpenseRow } from "@/components/expenses/ExpenseRow";
import { ExpenseDialogs } from "@/components/expenses/ExpenseDialogs";
import { SnapshotExportMenu } from "@/components/expenses/SnapshotExportMenu";
import { MediatorPdfDownloadButton } from "@/components/mediator/MediatorPdfDownloadButton";
import { toast } from "sonner";

const CATS = ["medical", "dental", "optical", "educational", "extra-curricular", "clothing", "travel", "childcare", "other"];
const CAT_LABEL = {
  medical: "Medical",
  dental: "Dental",
  optical: "Optical",
  educational: "Educational",
  "extra-curricular": "Extra-curricular",
  clothing: "Clothing",
  travel: "Travel",
  childcare: "Childcare",
  other: "Other",
  // legacy labels for pre-v11 data
  school: "School",
  education: "Educational",
  activities: "Extra-curricular",
  legal: "Legal",
};
const CAT_COLOR = {
  medical:            { bar: "#B4495A", chip: "bg-[#F7E4E7] text-[#7A2A36] border-[#E1B8BF]" },
  dental:             { bar: "#E57B8A", chip: "bg-[#FCE9EC] text-[#7A3D47] border-[#E8BFC5]" },
  optical:            { bar: "#8EB4CC", chip: "bg-[#E6EFF4] text-[#3D5C6B] border-[#B6CFDD]" },
  educational:        { bar: "#4A6FA5", chip: "bg-[#E3EAF3] text-[#2E4A73] border-[#B8C7DD]" },
  "extra-curricular": { bar: "#D9A441", chip: "bg-[#F7EDD4] text-[#7A5A1E] border-[#E0C88C]" },
  clothing:           { bar: "#7A5AA6", chip: "bg-[#ECE3F3] text-[#4E3877] border-[#C6B2DD]" },
  travel:             { bar: "#3D8A6B", chip: "bg-[#DFEDE5] text-[#22563F] border-[#A7CDB9]" },
  childcare:          { bar: "#C3447A", chip: "bg-[#F5DCE6] text-[#8A2651] border-[#DCA9C1]" },
  other:              { bar: "#6F828A", chip: "bg-[#E6EAEB] text-[#455258] border-[#BFC8CB]" },
  // Legacy fallbacks
  school:             { bar: "#4A6FA5", chip: "bg-[#E3EAF3] text-[#2E4A73] border-[#B8C7DD]" },
  education:          { bar: "#4A6FA5", chip: "bg-[#E3EAF3] text-[#2E4A73] border-[#B8C7DD]" },
  activities:         { bar: "#D9A441", chip: "bg-[#F7EDD4] text-[#7A5A1E] border-[#E0C88C]" },
  legal:              { bar: "#5F4B8B", chip: "bg-[#E5E0EE] text-[#3A2E5E] border-[#B9ACD0]" },
};
const catMeta = (k) => CAT_COLOR[k] || CAT_COLOR.other;
const catLabel = (k) => CAT_LABEL[k] || k;

const SPLIT_OPTIONS = [
  { value: "default", label: "Use agreed default" },
  { value: "50_50", label: "50 / 50" },
  { value: "60_40", label: "60 / 40 (payer gets 60%)" },
  { value: "70_30", label: "70 / 30 (payer gets 70%)" },
  // v77.99.63 — Sole-parent preset. Payer keeps 100%, co-parents are
  // not implicated in any reimbursement. Sits between the share-based
  // presets and the open-ended Custom option.
  { value: "100_0", label: "100 / 0 (payer keeps the whole cost)" },
  { value: "custom", label: "Custom payer share" },
];
// Precomputed once so JSX doesn't re-filter every render.
const SPLIT_OPTIONS_NON_DEFAULT = SPLIT_OPTIONS.filter((o) => o.value !== "default");
const PM_OPTIONS = [
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];
const PM_LABEL = Object.fromEntries(PM_OPTIONS.map((p) => [p.value, p.label]));

const STATUS_META = {
  outstanding: { label: "Outstanding", className: "border-[#825A41] bg-[#F0E8E3] text-[#4A3A2E]" },
  partial: { label: "Partially paid", className: "border-[#B88A3A] bg-[#FBF0DC] text-[#6F4E1E]" },
  pending_confirmation: { label: "Awaiting confirmation", className: "border-[#4A6FA5] bg-[#E3EAF3] text-[#2E4A73]" },
  paid: { label: "Paid", className: "border-[#1F9FA4] bg-[#D6F0EF] text-[#116B6E]" },
  voided: { label: "Voided", className: "border-[#8A8A8A] bg-[#EAEAEA] text-[#6A6A6A] line-through" },
};

// Fields that are safe + meaningful to surface on the user-facing Edit History.
// Everything else stays in the raw backend audit log but is hidden from the UI
// (no UUIDs, no internal booleans, no storage identifiers leaking through).
const EDIT_FIELD_LABELS = {
  title: "Title",
  amount: "Amount",
  category: "Category",
  date: "Date",
  split_type: "Split",
  split_percent: "Split %",
  paid_by: "Payer",
  notes: "Notes",
  all_children: "Child allocation",
  child_ids: "Child allocation",
};
// Returns an ordered, de-duplicated list of user-friendly change summaries.
// e.g. ["Amount updated", "Child allocation updated"]
function summariseEdit(diff) {
  if (!diff || typeof diff !== "object") return [];
  const labels = [];
  for (const k of Object.keys(diff)) {
    const label = EDIT_FIELD_LABELS[k];
    if (label && !labels.includes(label)) labels.push(label);
  }
  return labels;
}

// v77.99.61 — Per-parent breakdown extracted into its own component
// and collapsed by default. Mediator-level detail (paid upfront,
// share total, reimbursed, still owed) lives behind a one-tap
// disclosure so first-time users aren't drowned in 12+ numbers
// before reaching the expense list. Preference persists per user.
function PerMemberBreakdown({ summary, members, childList, user, currency, childFilter }) {
  const KEY = "cpc_expenses_per_member_open";
  const [open, setOpenState] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  const setOpen = (v) => {
    setOpenState(v);
    try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* ignore */ }
  };
  const entries = Object.entries(summary.per_member || {});
  if (entries.length === 0) return null;
  return (
    <div className="border border-[#D9E1E3] rounded-md bg-white" data-testid="per-member-breakdown-wrapper">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-[#F4FAFA] transition-colors rounded-md"
        data-testid="per-member-breakdown-toggle"
        aria-expanded={open}
        aria-controls="per-member-breakdown-body"
      >
        <div className="inline-flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-[#2E3A3F]">Per-parent breakdown</span>
          <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5 tabular-nums">
            {entries.length}
          </span>
        </div>
        <span className="inline-flex items-center text-xs text-[#6F828A] gap-1">
          {open ? <>Hide <ChevronUp className="w-4 h-4" strokeWidth={1.8} /></> : <>Show <ChevronDown className="w-4 h-4" strokeWidth={1.8} /></>}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 grid md:grid-cols-2 gap-4" id="per-member-breakdown-body" data-testid="per-member-breakdown">
          {entries.map(([uid, pm]) => {
            const m = members.find((x) => x.id === uid) || { id: uid, name: pm.name || "Co-parent", is_placeholder: false };
            const isYou = uid === user?.id;
            const memberColor = getParentColorById(uid, members);
            const scopedChild = childFilter !== "all"
              ? childList.find((c) => c.id === childFilter)
              : null;
            const isManagingOfScopedChild =
              !!scopedChild && scopedChild.created_by && scopedChild.created_by === uid;
            const accentLabel = isYou
              ? "You"
              : isManagingOfScopedChild
                ? "Managing parent"
                : "Co-parent";
            // v77.99.75 — Balance UI derivation extracted from a nested
            // ternary chain into named locals. Three branches: owed-to,
            // owed-by, settled.
            let balanceColorClass;
            let balanceText;
            if (pm.balance > 0) {
              balanceColorClass = "text-[#1F9FA4]";
              balanceText = `+${money(pm.balance, currency)} ${isYou ? "owed to you" : "owed to them"}`;
            } else if (pm.balance < 0) {
              balanceColorClass = "text-[#C0291D]";
              balanceText = `${money(pm.balance, currency)} ${isYou ? "you owe" : "they owe"}`;
            } else {
              balanceColorClass = "text-[#6F828A]";
              balanceText = "Settled";
            }
            // "Reimbursed" row label depends on payment direction.
            let reimbursedLabel;
            if (!isYou) reimbursedLabel = "Reimbursed to them";
            else if (pm.balance < 0) reimbursedLabel = "You reimbursed";
            else reimbursedLabel = "Reimbursed to you";
            return (
              <div
                key={uid}
                className="border border-[#D9E1E3] p-5 rounded-md border-l-4"
                style={{ borderLeftColor: memberColor, backgroundColor: tintBg(memberColor) }}
                data-testid={`per-member-${uid}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-sm text-white"
                      style={{ backgroundColor: memberColor }}
                      data-testid={`per-member-role-${uid}`}
                    >
                      {accentLabel}
                    </span>
                    <span className="font-heading text-base font-medium truncate">{m.name}</span>
                    {m.is_placeholder && <span className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">Placeholder</span>}
                  </div>
                  <span className={`text-xs font-mono-data ${balanceColorClass}`} data-testid={`per-member-balance-${uid}`}>
                    {balanceText}
                  </span>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Paid upfront</dt>
                    <dd className="font-mono-data tabular-nums">{money(pm.paid_by_them, currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{isYou ? "Your share total" : "Their share total"}</dt>
                    <dd className="font-mono-data tabular-nums">{money(pm.their_share, currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                      {reimbursedLabel}
                    </dt>
                    <dd className="font-mono-data tabular-nums">{money(pm.reimbursed_to_them, currency)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Still owed</dt>
                    <dd className={`font-mono-data tabular-nums ${pm.outstanding_owed > 0 ? "text-[#C0291D]" : "text-[#6F828A]"}`}>{money(pm.outstanding_owed, currency)}</dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



export default function Expenses() {
  const { user, currency, household, members, isMediator } = useAuth();
  const { selectedChildId, setSelectedChildId } = useChildScope();
  // v77.49 — Audit trail for third-party viewers.
  useEffect(() => {
    recordThirdPartyView({ resource_type: "expenses", resource_label: "Opened expenses page" });
  }, []);

  // v77.99.86 — Convert hand-off from /app/upcoming-expenses. The
  // dedicated page can't host the full Add Expense dialog so it
  // stashes the heads-up + linker on window and navigates here with
  // `?convert_upcoming=1`. We pick that up, hydrate the dialog, and
  // let the user finalise (receipt upload, split tweak, etc.).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("convert_upcoming") !== "1") return;
    const item = window.__upcomingConvertPrefill;
    if (!item) {
      // Stale link with no payload — silently strip the query.
      window.history.replaceState({}, "", "/app/expenses");
      return;
    }
    setForm((f) => ({
      ...f,
      title: item.title || "",
      category: item.category || "other",
      amount: String(item.amount || ""),
      date: new Date().toISOString().slice(0, 10),
      child_ids: item.child_ids || [],
      notes: item.notes || "",
      split_type: "default",
      split_payer_share: 50,
      paid_by: user?.id || f.paid_by,
      all_children: false,
    }));
    setOpen(true);
    // Clear the prefill but KEEP the linker — onCreate() reads it
    // after the expense saves and stamps the upcoming record.
    window.__upcomingConvertPrefill = null;
    window.history.replaceState({}, "", "/app/expenses");
  }, [user?.id]);

  const [items, setItems] = useState([]);
  // v77.99.150 (iter 122.loading-states) — Explicit loading flag so the
  // empty state ("No expenses yet.") never displays before the first
  // /expenses response arrives. Record-keeping products cannot afford
  // to show a false-negative empty state.
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [expanded, setExpanded] = useState({}); // {id: bool}

  const [open, setOpen] = useState(false);
  // v77.99.85 — Imperative ref to UpcomingExpensesSection so the
  // page-level "Flag upcoming expense" button can open the create
  // dialog without lifting all of that section's state up.
  const upcomingRef = useRef(null);
  const [receiptFile, setReceiptFile] = useState(null);
  // v77.99.12 — Guard against accidental double-submit of the Add
  // Expense form. Production reports showed identical-row duplicates
  // (same date, amount, split, no second receipt) — the signature of
  // a user tapping Save twice while the request is in flight on a
  // slow mobile connection. `createBusy` disables the button + the
  // submit handler refuses to re-enter while the previous POST is
  // still pending. We also generate a fresh per-form idempotency
  // key so even if a retry sneaks through (network blip, double-tap
  // racing the disabled state), the backend collapses duplicates.
  const [createBusy, setCreateBusy] = useState(false);
  const [createIdemKey, setCreateIdemKey] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const [form, setForm] = useState({
    title: "", category: "other", amount: "", date: new Date().toISOString().slice(0, 10),
    split_type: "default", split_payer_share: 50, paid_by: user?.id || "", notes: "",
    child_ids: [], all_children: false,
  });

  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  // Iter 125.receipt-docket-layout — Persist the user's preferred
  // export layout across sessions. `standard` = vertically-stacked
  // header + image (default, backwards-compatible). `docket` = the
  // court-exhibit style: metadata card on the LEFT, image on the
  // RIGHT of every receipt page. Solicitors and mediators tend to
  // prefer docket; casual internal downloads tend to prefer
  // standard. localStorage keeps the choice sticky per browser.
  const [exportLayout, setExportLayout] = useState(() => {
    try {
      const v = window.localStorage.getItem("cpc.expensesExportLayout");
      return v === "docket" ? "docket" : "standard";
    } catch {
      return "standard";
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("cpc.expensesExportLayout", exportLayout);
    } catch {
      /* localStorage disabled — non-fatal */
    }
  }, [exportLayout]);
  // Iter 123.hotfix — Period default + persistence.
  //
  // Prior default was "this_month", which silently hid every
  // historical expense on households whose most recent entry was
  // more than 30 days old. Symptom: Dashboard showed a real
  // outstanding balance while the Expenses page rendered
  // €0.00 / "everything settled" from the same records.
  //
  // Fix (per product):
  //   * FIRST visit → "last_12m" (Last 12 months). Broad enough that
  //     the vast majority of households see their real balance
  //     immediately; still bounded so extremely old records don't
  //     bloat the initial render.
  //   * If localStorage carries a prior selection, honour it — even
  //     across sessions and devices — so returning users keep the
  //     window they last chose.
  //   * If the user picks a different chip, persist the choice so
  //     the next visit remembers it. Custom ranges are persisted
  //     alongside so the exact dates come back too.
  const PERIOD_STORAGE_KEY = "coparent.expenses.period.v1";
  const CUSTOM_RANGE_STORAGE_KEY = "coparent.expenses.customRange.v1";
  const [period, setPeriod] = useState(() => {
    try {
      const saved = window.localStorage.getItem(PERIOD_STORAGE_KEY);
      const valid = ["this_month", "ytd", "this_year", "last_12m", "all", "custom"];
      if (saved && valid.includes(saved)) return saved;
    } catch (_) { /* no-op — private mode / disabled storage */ }
    // Iter 123.reconcile — First-visit default is "all" so the
    // Expenses page reconciles exactly with the Dashboard money
    // card (which shows all-time viewer's net outstanding). Users
    // narrow via the chip strip; their choice is remembered
    // across sessions in localStorage.
    return "all";
  });
  const [customRange, setCustomRange] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CUSTOM_RANGE_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return { from: parsed.from || "", to: parsed.to || "" };
      }
    } catch (_) { /* no-op */ }
    return { from: "", to: "" };
  });
  useEffect(() => {
    try { window.localStorage.setItem(PERIOD_STORAGE_KEY, period); } catch (_) { /* no-op */ }
  }, [period]);
  useEffect(() => {
    try {
      window.localStorage.setItem(CUSTOM_RANGE_STORAGE_KEY, JSON.stringify(customRange || {}));
    } catch (_) { /* no-op */ }
  }, [customRange]);
  const activeRange = useMemo(() => computeRange(period, customRange), [period, customRange]);
  const [activeTab, setActiveTab] = useState("expenses"); // "expenses" | "payments"
  // Iter 122.exp-segmented — When the user toggles tabs we want the
  // page to feel like two distinct views sharing one header. We
  // (a) gate the action bar + summary cards + lists by activeTab,
  // (b) wrap the tab content in a keyed div so React remounts the
  // subtree and the CSS `animate-in fade-in` runs on each switch, and
  // (c) scrollIntoView the tab content top so the user sees the
  // change above the fold rather than half-way down the page.
  const tabContentRef = useRef(null);
  const handleTabChange = useCallback((next) => {
    setActiveTab(next);
    requestAnimationFrame(() => {
      try {
        tabContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { /* older browsers — silent no-op */ }
    });
  }, []);
  // v77.99.106 — Discoverability hand-off from a debtor clicking the
  // "credit available" pill on an open expense row → Payments tab
  // auto-opens the AllocateDialog pre-targeting that expense.
  const [pendingAllocationTarget, setPendingAllocationTarget] = useState(null);
  const [children, setChildren] = useState([]);
  // v76.3 — Child filter is the GLOBAL ChildSwitcher state. The on-page
  // chip bar reads from / writes to the same source so changing scope
  // anywhere in the app is consistent. "All" = `selectedChildId == null`.
  const childFilter = selectedChildId || "all";
  const setChildFilter = (v) => setSelectedChildId(v === "all" ? null : v);

  const [settleOpen, setSettleOpen] = useState(false);
  const [settleBusy, setSettleBusy] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentRef, setPaymentRef] = useState("");

  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", method: "bank_transfer", reference: "", note: "", date: new Date().toISOString().slice(0, 10) });

  const [receiptFor, setReceiptFor] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ amount: "", method: "bank_transfer", reference: "", note: "", date: new Date().toISOString().slice(0, 10) });
  const [receiptBusy, setReceiptBusy] = useState(false);

  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);

  const [voidFor, setVoidFor] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  // v77 — Multi-receipt attach. `attachFor` holds the expense the user is
  // attaching to; `attachFile` is the staged File object until they confirm.
  // Removal is fire-and-forget (with a confirm) and reloads the list.
  const [attachFor, setAttachFor] = useState(null);
  const [attachFile, setAttachFile] = useState(null);
  const [attachBusy, setAttachBusy] = useState(false);

  const load = async () => {
    // v77.99.104 — Pass period range to backend so /expenses,
    // /expenses/summary and /settlements all respect the chosen
    // window. Falls back to no-range (everything) when range is empty.
    const params = childFilter !== "all" ? { child_id: childFilter } : {};
    if (activeRange.from) params.from_date = activeRange.from;
    if (activeRange.to) params.to_date = activeRange.to;
    try {
      const [ex, sum, sts, ch] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expenses/summary", { params }),
        // v77.60 — Pass `child_id` so the past-settlements section
        // only includes settlements that actually cover this child.
        // Without this, a child-specific page leaks settlements for
        // other children the user co-parents — exactly the surface
        // the user reported.
        api.get("/settlements", { params }).catch(() => ({ data: [] })),
        api.get("/children?include_archived=false").catch(() => ({ data: [] })),
      ]);
      setItems(ex.data);
      setSummary(sum.data);
      setSettlements(sts.data || []);
      setChildren(ch.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [childFilter, activeRange.from, activeRange.to]);

  const defaultSplit = household?.default_split;
  const defaultSplitLabel = useMemo(() => {
    if (!defaultSplit) return "50 / 50";
    if (defaultSplit.split_type === "custom") return "Custom";
    return defaultSplit.split_type.replace("_", " / ");
  }, [defaultSplit]);

  // v77.10 — debtorsLabelByPayer (household-wide list) was retired in
  // favour of `eligibleParentsFor(child_ids)` so the "owes" labels are
  // child-scoped per expense instead of leaking unrelated parents.

  const onCreate = async (e) => {
    e.preventDefault();
    // v77.99.12 — Block re-entry while a previous create is in flight.
    // Belt-and-braces against the disabled-button state racing with a
    // double tap.
    if (createBusy) return;
    // Phase 3 Delivery B — explicit child selection required when the
    // household has any children. The legacy "Applies to all children"
    // toggle has been removed; users now multi-select chips instead.
    const hasChildren = (children || []).length > 0;
    if (hasChildren && (!form.child_ids || form.child_ids.length === 0)) {
      toast.error("Please pick at least one child for this expense.");
      return;
    }
    setCreateBusy(true);
    try {
      let receipt_file_id = null;
      if (receiptFile) {
        const fd = new FormData(); fd.append("file", receiptFile);
        const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_file_id = up.data.id;
      }
      const payload = { ...form, amount: parseFloat(form.amount), receipt_file_id };
      if (payload.split_type !== "custom") delete payload.split_payer_share;
      // `all_children` is deprecated; the server will reset it to false
      // regardless. Strip it locally so the picker doesn't bleed back in.
      delete payload.all_children;
      // v77.99.12 — Idempotency-Key header. Backend returns the
      // existing expense if it sees the same key within the household,
      // so a double-fire (retry, brief network glitch, or any future
      // multi-tap path) cannot create a duplicate row.
      const createdResp = await api.post("/expenses", payload, {
        headers: { "Idempotency-Key": createIdemKey },
      });
      // v77.99.80 — If this expense came from an "Upcoming heads-up
      // → Mark as paid" flow, stamp the heads-up as converted so it
      // disappears from the active list. The linker callback was
      // installed by UpcomingExpensesSection.handleMarkPaid.
      const linker = typeof window !== "undefined" ? window.__pendingHeadsUpLink : null;
      if (linker && createdResp?.data?.id) {
        try { await linker(createdResp.data.id); } catch { /* toast surfaced inside linker */ }
        if (typeof window !== "undefined") window.__pendingHeadsUpLink = null;
      }
      toast.success("Expense added");
      setOpen(false);
      setForm({ title: "", category: "other", amount: "", date: new Date().toISOString().slice(0, 10), split_type: "default", split_payer_share: 50, paid_by: user?.id || "", notes: "", child_ids: selectedChildId ? [selectedChildId] : [], all_children: false });
      setReceiptFile(null);
      // Rotate the idempotency key so the NEXT expense gets its own.
      setCreateIdemKey(
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setCreateBusy(false);
    }
  };

  // v77.99.114 — Credit-adjusted remaining helper. Subtract any
  // already-applied credit allocations from the gross
  // outstanding_to_payer so payment dialogs pre-fill the REAL amount
  // still owed (without the credit-allocation duplication that
  // caused the production double-credit bug).
  const _creditAdjustedRemaining = (exp) => {
    const gross = exp?.breakdown?.outstanding_to_payer || 0;
    const allocs = exp?.credit_allocations || [];
    const allocSum = allocs.reduce((s, a) => s + (a.amount || 0), 0);
    return Math.max(0, gross - allocSum);
  };
  const openPay = (exp) => {
    const remaining = _creditAdjustedRemaining(exp);
    setPayFor(exp);
    setPayForm({ amount: remaining.toFixed(2), method: "bank_transfer", reference: "", note: "", date: new Date().toISOString().slice(0, 10) });
  };
  const submitPay = async () => {
    try {
      const payload = {
        amount: parseFloat(payForm.amount),
        method: payForm.method,
        reference: payForm.reference,
        note: payForm.note,
        date: payForm.date,
      };
      await api.post(`/expenses/${payFor.id}/payments`, payload);
      toast.success("Payment logged — awaiting confirmation");
      setPayFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const removePayment = async (exp, pid) => {
    if (!window.confirm("Remove this unconfirmed payment entry?")) return;
    try {
      await api.delete(`/expenses/${exp.id}/payments/${pid}`);
      toast.success("Payment removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v77.99.115g — User-facing "Reverse duplicate" — the customer's
  // self-serve version of the admin remediation. Available on a
  // direct-payment row when the same expense ALSO has a credit
  // allocation (i.e. the same money has been counted twice). Voids
  // the duplicate payment with a clear, audit-friendly reason.
  const reverseDuplicate = async (exp, pid) => {
    const ok = window.confirm(
      "Reverse this payment?\n\n"
      + "This expense is already covered by an allocated payment credit, "
      + "so recording this direct payment double-counted the same money. "
      + "Reversing keeps the credit allocation as the truthful settlement "
      + "and voids the duplicate direct payment. Audit-logged.\n\n"
      + "The payment record stays in the history (marked voided)."
    );
    if (!ok) return;
    try {
      await api.post(`/expenses/${exp.id}/payments/${pid}/reverse-duplicate`);
      toast.success("Duplicate payment reversed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reverse");
    }
  };

  const openReceipt = (exp) => {
    const remaining = _creditAdjustedRemaining(exp);
    setReceiptFor(exp);
    setReceiptForm({ amount: remaining.toFixed(2), method: "bank_transfer", reference: "", note: "", date: new Date().toISOString().slice(0, 10) });
  };
  const submitReceipt = async () => {
    if (!receiptFor) return;
    setReceiptBusy(true);
    try {
      await api.post(`/expenses/${receiptFor.id}/record-receipt`, {
        amount: parseFloat(receiptForm.amount),
        method: receiptForm.method,
        reference: receiptForm.reference,
        note: receiptForm.note,
        date: receiptForm.date,
      });
      toast.success("Receipt recorded");
      setReceiptFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setReceiptBusy(false); }
  };

  // One-click "Mark fully settled" — semantic depends on role:
  //   payer → record-receipt (auto-confirmed, single-sided)
  //   debtor → mark-paid (needs payer confirmation, two-sided audit)
  const quickSettle = async (exp, asPayer) => {
    const remaining = _creditAdjustedRemaining(exp);
    if (remaining <= 0.005) {
      const allocs = exp?.credit_allocations || [];
      const allocSum = allocs.reduce((s, a) => s + (a.amount || 0), 0);
      if (allocSum > 0.005) {
        toast.info("This expense is already fully covered by a credit allocation. Remove the allocation first if you need to log a separate direct payment.");
      }
      return;
    }
    const msg = asPayer
      ? `Mark this expense as fully settled? This will record receipt of ${money(remaining, currency)} (${PM_LABEL.bank_transfer}) on today's date. You can add method/reference details instead by using "Record payment received".`
      : `Mark this expense as fully settled? This will log a payment of ${money(remaining, currency)} for your co-parent to confirm.`;
    if (!window.confirm(msg)) return;
    try {
      const payload = { amount: remaining, method: "bank_transfer", reference: "", note: "Marked fully settled (quick action)", date: new Date().toISOString().slice(0, 10) };
      if (asPayer) await api.post(`/expenses/${exp.id}/record-receipt`, payload);
      else await api.post(`/expenses/${exp.id}/mark-paid`);
      toast.success("Marked as fully settled");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const [confirmFor, setConfirmFor] = useState(null); // {exp, payment}
  const [disputeReason, setDisputeReason] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const openConfirm = (exp, payment) => { setConfirmFor({ exp, payment }); setDisputeReason(""); };
  const doConfirm = async () => {
    if (!confirmFor) return;
    setConfirmBusy(true);
    try {
      await api.post(`/expenses/${confirmFor.exp.id}/payments/${confirmFor.payment.id}/confirm`);
      toast.success("Payment confirmed");
      setConfirmFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setConfirmBusy(false); }
  };
  const doDispute = async () => {
    if (!confirmFor) return;
    if (!disputeReason.trim()) { toast.error("Please explain why you haven't received this payment"); return; }
    setConfirmBusy(true);
    try {
      await api.post(`/expenses/${confirmFor.exp.id}/payments/${confirmFor.payment.id}/dispute`, { reason: disputeReason.trim() });
      toast.success("Payment marked as not received");
      setConfirmFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setConfirmBusy(false); }
  };

  const openEdit = (exp) => {
    setEditFor(exp);
    setEditForm({
      title: exp.title || "",
      category: exp.category || "other",
      amount: String(exp.amount ?? ""),
      date: exp.date || new Date().toISOString().slice(0, 10),
      split_type: exp.split_type || "50_50",
      split_payer_share: exp.split_payer_share ?? 50,
      paid_by: exp.paid_by || "",
      notes: exp.notes || "",
      child_ids: Array.isArray(exp.child_ids) ? exp.child_ids : [],
      all_children: !!exp.all_children,
    });
  };
  const submitEdit = async (force = false) => {
    setEditBusy(true);
    try {
      const payload = {
        title: editForm.title,
        category: editForm.category,
        amount: parseFloat(editForm.amount),
        date: editForm.date,
        split_type: editForm.split_type,
        paid_by: editForm.paid_by,
        notes: editForm.notes,
        child_ids: editForm.all_children ? [] : editForm.child_ids,
        all_children: editForm.all_children,
        force,
      };
      if (payload.split_type === "custom") payload.split_payer_share = parseFloat(editForm.split_payer_share);
      await api.put(`/expenses/${editFor.id}`, payload);
      toast.success("Expense updated");
      setEditFor(null);
      setEditForm(null);
      load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Failed";
      if (status === 409 && !force && detail.toLowerCase().includes("force")) {
        if (window.confirm("This expense already has reimbursement payments logged against it. Editing financial fields (amount, split, or who paid) will recalculate what's owed. Continue?")) {
          setEditBusy(false);
          return submitEdit(true);
        }
      } else {
        toast.error(detail);
      }
    } finally { setEditBusy(false); }
  };

  const openVoid = (exp) => { setVoidFor(exp); setVoidReason(""); };
  const submitVoid = async () => {
    if (!voidReason.trim()) { toast.error("Please enter a reason"); return; }
    try {
      await api.post(`/expenses/${voidFor.id}/void`, { reason: voidReason.trim() });
      toast.success("Expense voided");
      setVoidFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const unvoid = async (exp) => {
    if (!window.confirm("Restore this voided expense?")) return;
    try {
      await api.post(`/expenses/${exp.id}/unvoid`);
      toast.success("Expense restored");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v77 — Receipt visibility helpers.
  // Combines the legacy single-id field with the canonical array so we can
  // count + render even on rows that pre-date the migration (defensive —
  // backend startup also backfills, but UI shouldn't trust that timing).
  const receiptIds = (exp) => {
    const out = [];
    const seen = new Set();
    if (exp?.receipt_file_id && !seen.has(exp.receipt_file_id)) {
      out.push(exp.receipt_file_id); seen.add(exp.receipt_file_id);
    }
    for (const rid of (exp?.receipt_file_ids || [])) {
      if (rid && !seen.has(rid)) { out.push(rid); seen.add(rid); }
    }
    return out;
  };

  const openAttach = (exp) => { setAttachFor(exp); setAttachFile(null); };
  const submitAttach = async (e) => {
    e?.preventDefault?.();
    if (!attachFor || !attachFile) return;
    setAttachBusy(true);
    try {
      const fd = new FormData(); fd.append("file", attachFile);
      const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/expenses/${attachFor.id}/receipts`, { file_id: up.data.id });
      toast.success("Receipt attached");
      setAttachFor(null);
      setAttachFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach receipt");
    } finally {
      setAttachBusy(false);
    }
  };
  const detachReceipt = async (exp, fileId) => {
    if (!window.confirm("Remove this receipt? The file stays in the audit log but the link to this expense will be cleared.")) return;
    try {
      await api.delete(`/expenses/${exp.id}/receipts/${fileId}`);
      toast.success("Receipt removed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // v77.4 — Toggle "No receipt needed". Stored on the expense as
  // `receipt_not_required`. The badge derives its visual state from the
  // resulting payload, so a single API call → consistent UI everywhere.
  const setNotRequired = async (exp, value) => {
    try {
      await api.post(`/expenses/${exp.id}/receipt-not-required`, { not_required: !!value });
      toast.success(value ? "Marked as no receipt needed" : "Receipt status reset");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const settleAll = async () => {
    setSettleBusy(true);
    try {
      const { data } = await api.post("/expenses/settle-all", { payment_method: paymentMethod, payment_reference: paymentRef });
      if (data.settled_count === 0) toast.info("Nothing to settle");
      else toast.success(`Settled ${data.settled_count} expense${data.settled_count === 1 ? "" : "s"}`);
      setSettleOpen(false);
      setPaymentRef("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSettleBusy(false); }
  };

  const nameOf = (id) => members.find((m) => m.id === id)?.name || "—";
  const firstName = (id) => (nameOf(id) || "").split(" ")[0];

  // v77.61 — Managing-parent helper. Mirrors the backend's
  // `_is_child_managing_parent`: a user manages a child when they
  // created it (`created_by == user.id`) or, for legacy children
  // without a `created_by` tag, when they're the household creator.
  // Used to pre-fill `paid_by` and to tag the user's option in the
  // payer dropdown so they immediately understand why they can pay.
  const isManagingParentOf = (child) => {
    if (!child || !user?.id) return false;
    const creator = child.created_by;
    if (creator) return creator === user.id;
    return !!user?.is_primary_parent;
  };

  // True iff the user manages EVERY child in `childIds`. Used as
  // the "default payer to self" gate so a multi-child expense only
  // pre-fills the payer when the user has full ownership across the
  // whole selection.
  const userManagesAll = (childIds) => {
    if (!childIds || childIds.length === 0) return false;
    return childIds.every((cid) => {
      const child = children.find((c) => c.id === cid);
      return isManagingParentOf(child);
    });
  };

  // v77.10 — Relationship-driven helper. Returns the subset of
  // household members eligible to participate in an expense covering
  // the given child_ids. Backed by the new `parent_ids` field on each
  // child doc (server-side authoritative). When no children are picked
  // (e.g. "All children" mode) we fall back to every household member
  // — that matches the backend's eligibility rules for unscoped views.
  // The placeholder co-parent is always retained so single-real-user
  // households still see a co-parent dropdown option pre-invite.
  const eligibleParentsFor = (childIds) => {
    if (!childIds || childIds.length === 0) return members;
    const ids = new Set();
    for (const cid of childIds) {
      const child = children.find((c) => c.id === cid);
      for (const pid of (child?.parent_ids || [])) ids.add(pid);
    }
    // Defensive: if children haven't been hydrated yet, don't strip
    // everyone out — render the full list rather than an empty
    // dropdown that would block expense creation.
    if (ids.size === 0) return members;
    const placeholders = members.filter((m) => m.is_placeholder);
    const real = members.filter((m) => ids.has(m.id));
    return [...real, ...placeholders];
  };

  const splitLabel = (x) => {
    if (x.split_type === "custom" && x.split_payer_share) return `${Math.round(x.split_payer_share)}/${100 - Math.round(x.split_payer_share)}`;
    if (x.split_type === "custom") return "custom";
    return (x.split_type || "50_50").replace("_", "/");
  };

  const exportMonthly = () => {
    // v77.99.159 (iter 137.expenses-standard-vs-detailed) — Pass the
    // selected report type through to both the sync URL and the
    // async-fallback payload. Backend accepts `detailed` (canonical)
    // and `docket` (legacy alias) interchangeably — we send
    // `detailed` on the wire so the server-side request body +
    // retry snapshot reflect the user-facing name.
    const serverLayout = exportLayout === "docket" ? "detailed" : "standard";
    const qs = new URLSearchParams({ month });
    if (serverLayout !== "standard") qs.set("layout", serverLayout);
    downloadFile(
      `${API}/expenses/export/pdf?${qs.toString()}`,
      `expenses-${month}${serverLayout === "detailed" ? "-detailed" : ""}.pdf`,
      {
        label: serverLayout === "detailed"
          ? "Generating detailed expenses PDF"
          : "Generating expenses PDF",
        successMessage: "PDF ready",
        asyncFallback: {
          endpoint: "/exports/expenses/pdf/start",
          // Persist `layout` on the async job's `params` so retries
          // rehydrate the SAME report type.
          payload: { month, layout: serverLayout },
        },
      },
    );
  };

  // v77.99.20 — Snapshot range presets (current month, previous month,
  // year to date, last 12 months, custom). The dropdown menu component
  // owns the picker UX + the custom-range dialog; we only pass it the
  // active child filter. Date-range math lives in
  // `/lib/snapshotPresets.js` for easy future extensions (school year,
  // financial year, per-child annual).

  const outstandingCount = items.filter((x) => x.status === "outstanding" || x.status === "partial").length;

  // v77.1 — Missing-receipt filter wired to ?missing_receipt=1 so the
  // dashboard tile (and any future external link) can deep-link straight
  // to the rows that need attention. The filter scopes to the CURRENT
  // user's expenses to keep accountability personal — chasing someone
  // else's paperwork is friction.
  // v77.89 — Added a category dropdown and a generic "no receipts attached"
  // toggle. The two existing missing-receipt entry points stay as-is:
  //   • Dashboard tile / ?missing_receipt=1 — scoped to the current user
  //     (personal accountability), AS BEFORE.
  //   • New "no-receipts" toggle on the page — scoped to ALL visible
  //     expenses regardless of who paid (a parent reviewing their
  //     co-parent's submissions also benefits).
  // The two filters compose: combining them yields "no receipts AND
  // mine only" which is identical to the legacy missing-receipt view.
  const {
    visibleItems,
    missingOnly,
    categoryFilter,
    noReceiptsOnly,
    statusFilter,
    filtersActive,
    clearMissingFilter,
    setCategoryFilter,
    toggleNoReceipts,
    setStatusFilter,
    clearAllFilters,
  } = useExpensesFilters(items, user?.id);

  // Row-level context + actions. Passed as two grouped props to
  // <ExpenseRow> so its prop surface stays narrow even though the row
  // needs ~12 callbacks and ~7 derived helpers/constants.
  const rowCtx = {
    user, members, children, currency,
    STATUS_META, catMeta, PM_LABEL, summariseEdit,
    splitLabel, firstName, nameOf, eligibleParentsFor,
    unallocatedCredit: summary?.unallocated_credit || 0,
  };
  const rowActions = {
    toggleExpand: (id) => setExpanded((s) => ({ ...s, [id]: !s[id] })),
    openAttach, openReceipt, openPay, openConfirm, openEdit, openVoid,
    setNotRequired, quickSettle, unvoid,
    removePayment, detachReceipt, reverseDuplicate,
    applyCreditToExpense: (exp) => {
      const owed = exp?.breakdown?.outstanding_to_payer || 0;
      const credit = summary?.unallocated_credit || 0;
      const suggestedAmount = Math.min(owed, credit);
      setPendingAllocationTarget({ expenseId: exp.id, suggestedAmount });
      handleTabChange("payments");
    },
    // v77.99.109 — Reverse a single credit allocation from the
    // expense detail. The credit_id is part of the allocation row
    // returned by GET /expenses (see backend credit_alloc_map).
    removeAllocation: async (allocation) => {
      if (!window.confirm(
        `Remove this ${allocation.auto ? "auto-allocated " : ""}credit of ${
          allocation.currency || currency} ${allocation.amount.toFixed(2)
        }? The expense's outstanding amount will go back up.`
      )) return;
      try {
        await api.delete(
          `/expenses/payments/credits/${allocation.credit_id}/allocations/${allocation.id}`,
        );
        toast.success("Credit allocation removed.");
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not remove allocation.");
      }
    },
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* v77.61 — Scoped-view breadcrumb chip. When the topbar
          ChildScope is locked to a specific child, surface a small
          chip that explains "you're only seeing Child X's expenses"
          + a one-tap path back to the All-children rollup. Hidden in
          All-children mode where it would be noise. */}
      <ScopeBreadcrumb
        selectedChildId={selectedChildId}
        childList={children}
        onClear={() => setSelectedChildId(null)}
      />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Shared · {currency}</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1 inline-flex items-center gap-3 flex-wrap">Expenses<ChildScopeBadge testid="expenses-child-scope-badge" /></h1>
          <p className="text-xs text-[#6F828A] mt-1 inline-flex items-center gap-1.5" data-testid="expenses-default-split-line">
            Default split: <span className="font-mono-data">{defaultSplitLabel}</span>
            <InfoTooltip
              testid="expenses-default-split-tooltip"
              text="The full amount you log is treated as paid upfront by you — the app calculates what your co-parent owes back."
            />
          </p>
          {/* v77.99.102 (iter 122.exp1) — Period filter chip bar + tabs.
              Compact "Period" label keeps the focus on the headline
              + summary block above. PDF exports live inside a
              collapsible panel below. */}
          <div className="mt-3 space-y-2">
            <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">
              Period
            </div>
            <PeriodChipBar
              value={period}
              onChange={setPeriod}
              customRange={customRange}
              onCustomChange={setCustomRange}
              currency={currency}
            />
            <div className="flex gap-1 border-b border-[#D9E1E3] -mb-px">
              <button
                type="button"
                onClick={() => handleTabChange("expenses")}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "expenses"
                    ? "border-[#116B6E] text-[#116B6E]"
                    : "border-transparent text-[#6F828A] hover:text-[#2E3A3F]"
                }`}
                role="tab"
                aria-selected={activeTab === "expenses"}
                aria-controls="expenses-tab-content"
                data-testid="expenses-tab-expenses"
              >
                Expenses
              </button>
              <button
                type="button"
                onClick={() => handleTabChange("payments")}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                  activeTab === "payments"
                    ? "border-[#116B6E] text-[#116B6E]"
                    : "border-transparent text-[#6F828A] hover:text-[#2E3A3F]"
                }`}
                role="tab"
                aria-selected={activeTab === "payments"}
                aria-controls="expenses-tab-content"
                data-testid="expenses-tab-payments"
              >
                <Receipt className="w-3.5 h-3.5" strokeWidth={1.6} />
                Payments received
                {summary && summary.unallocated_credit > 0.005 && (
                  <span className="ml-1 text-[10px] uppercase tracking-[0.08em] font-semibold text-[#0F5658] bg-[#D6F0EF] border border-[#A7D8DA] rounded-sm px-1.5 py-0.5">
                    {currency} {summary.unallocated_credit.toFixed(2)}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
        {/* Iter 122.exp-segmented — The "Add expense" / "Flag upcoming
            expense" actions are CONTEXTUAL to the Expenses tab. On the
            Payments tab the PaymentCreditsPanel has its own primary
            "Record payment received" button, so we hide this group
            entirely there to avoid two competing primary CTAs. */}
        <div
          className={`flex flex-wrap items-center gap-2 transition-opacity ${
            activeTab === "expenses" ? "opacity-100" : "opacity-0 pointer-events-none h-0 overflow-hidden"
          }`}
          aria-hidden={activeTab !== "expenses"}
          data-testid="expenses-actions"
        >
          {/* v77.99.102 (iter 122.exp1) — Hierarchy: Add expense is the
              primary filled CTA (order-1, rendered first). Flag upcoming
              is the secondary outlined action (order-2). */}
          {!isMediator && (
            <Button
              variant="outline"
              className="order-2 border-[#B88A3A] text-[#6F4E1E] hover:bg-[#FBF0DC] rounded-sm"
              data-testid="add-upcoming-expense-top-btn"
              onClick={() => upcomingRef.current?.openCreate()}
            >
              <CalendarClock className="w-4 h-4 mr-2" /> Flag upcoming expense
            </Button>
          )}
          <Dialog open={open && !isMediator} onOpenChange={(v) => {
            if (isMediator) return;
            setOpen(v);
            // v77.99.81 — Clear any stashed heads-up linker on dialog
            // close so a cancelled "Mark as paid" flow can't bind the
            // user's NEXT unrelated expense save to the wrong heads-up.
            // v77.99.82 — Also reset the form fields to defaults so the
            // next 'Add expense' opens with a clean slate rather than
            // the cancelled heads-up's prefilled title/amount (iter84
            // cosmetic feedback).
            if (!v && typeof window !== "undefined") {
              if (window.__pendingHeadsUpLink) {
                window.__pendingHeadsUpLink = null;
                setForm({
                  title: "", category: "other", amount: "",
                  date: new Date().toISOString().slice(0, 10),
                  split_type: "default", split_payer_share: 50,
                  paid_by: user?.id || "", notes: "",
                  child_ids: selectedChildId ? [selectedChildId] : [],
                  all_children: false,
                });
              }
            }
            if (v) {
              // v77.99.85 — If the dialog is being opened via the
              // heads-up "Convert to expense" path, the convert handler
              // has already prefilled the form (title/amount/date/
              // child_ids/notes) and stashed a linker on window. In that
              // case do NOT clobber the prefilled child_ids with the
              // ChildScope default.
              const hasPendingHeadsUp = typeof window !== "undefined" && !!window.__pendingHeadsUpLink;
              if (!hasPendingHeadsUp) {
                // Pre-fill child_ids from the active scope. In master view
                // (no pinned child) the user must explicitly pick.
                const initialChildIds = selectedChildId ? [selectedChildId] : [];
                // v77.61 — When the user is the managing parent of the
                // pre-filled child, default the payer to themselves.
                // Saves a tap and removes confusion about who can pay
                // for children they own.
                const shouldDefaultSelf = initialChildIds.length > 0 && userManagesAll(initialChildIds);
                setForm((f) => ({
                  ...f,
                  child_ids: initialChildIds,
                  paid_by: shouldDefaultSelf ? user?.id || f.paid_by : f.paid_by,
                }));
              }
            }
          }}>
            {!isMediator && (
              <DialogTrigger asChild>
                <Button className="order-1 bg-[#1F9FA4] hover:bg-[#178085] rounded-sm" data-testid="add-expense-button">
                  <Plus className="w-4 h-4 mr-2" /> Add expense
                </Button>
              </DialogTrigger>
            )}
            <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]">
              <DialogHeader><DialogTitle className="font-heading">New expense</DialogTitle></DialogHeader>
              <UpgradePrompt feature="log expenses" testid="expenses-upgrade-prompt" />
              <form onSubmit={onCreate} className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="expense-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Amount ({currencySymbol(currency)})</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="expense-amount-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="expense-date-input" /></div>
                  <div>
                    <Label>Paid upfront by</Label>
                    <Select value={form.paid_by} onValueChange={(v) => setForm({ ...form, paid_by: v })}>
                      <SelectTrigger data-testid="expense-paidby-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {/* v77.10 — Restrict to parents linked to the
                            selected child(ren). The backend rejects
                            ineligible payers with a 400 anyway, but
                            scoping the dropdown is a friendlier UX.
                            v77.61 — Tag the user's own row with
                            "(you, managing parent)" when they own
                            every selected child, so the user
                            instantly understands why they're a valid
                            payer for children they manage. */}
                        {eligibleParentsFor(form.child_ids).map((m) => {
                          const isSelf = m.id === user?.id;
                          const showTag = isSelf && userManagesAll(form.child_ids);
                          return (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name}{showTag ? " (you, managing parent)" : isSelf ? " (you)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="inline-flex items-center gap-1.5">
                    Split
                    <InfoTooltip
                      text="50/50 splits the amount equally. Custom lets you set your own percentage. The full amount is treated as paid upfront by you; the app calculates what your co-parent owes back."
                      testid="help-tooltip-split-type"
                    />
                  </Label>
                  <Select value={form.split_type} onValueChange={(v) => setForm({ ...form, split_type: v })}>
                    <SelectTrigger data-testid="expense-split-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SPLIT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {form.split_type === "custom" && (
                  <div>
                    <Label>Payer share (%)</Label>
                    <Input type="number" min={0} max={100} value={form.split_payer_share} onChange={(e) => setForm({ ...form, split_payer_share: parseFloat(e.target.value) })} data-testid="expense-payer-share-input" />
                    <div className="text-xs text-[#6F828A] mt-1">Co-parent receives {100 - (Number(form.split_payer_share) || 0)}% of the bill</div>
                  </div>
                )}
                <ChildAssignPicker
                  childList={children}
                  allChildren={form.all_children}
                  childIds={form.child_ids}
                  onChange={(patch) => {
                    // v77.61 — When the user toggles into a state
                    // where they manage every selected child AND
                    // they haven't already chosen a different payer,
                    // pre-fill themselves as payer. We don't
                    // OVERWRITE an explicit user choice — only fill
                    // when the field is empty or already set to
                    // self.
                    const next = { ...form, ...patch };
                    const nextChildIds = next.all_children
                      ? children.map((c) => c.id)
                      : (next.child_ids || []);
                    if (
                      nextChildIds.length > 0 &&
                      userManagesAll(nextChildIds) &&
                      (!form.paid_by || form.paid_by === user?.id)
                    ) {
                      patch = { ...patch, paid_by: user?.id };
                    }
                    setForm({ ...form, ...patch });
                  }}
                  testPrefix="expense"
                />
                <div>
                  <Label className="flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> Receipt (optional)</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} data-testid="expense-receipt-input" />
                </div>
                <DialogFooter><Button type="submit" disabled={createBusy} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="expense-save-button">{createBusy ? "Saving…" : "Save expense"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Iter 122.exp-segmented — Tab content. The `key={activeTab}`
          forces a remount on every toggle so the CSS `animate-in
          fade-in` runs each time, giving immediate visual feedback
          that the page has switched view. The ref anchors a smooth
          scroll-into-view from handleTabChange so the user lands at
          the top of the new section without manual scrolling. */}
      <div
        ref={tabContentRef}
        id="expenses-tab-content"
        role="tabpanel"
        key={activeTab}
        data-testid={`expenses-tabpanel-${activeTab}`}
        className="space-y-6 animate-in fade-in-0 slide-in-from-top-1 duration-200"
      >
      {/* Iter 122.exp-segmented — Summary cards are tab-specific. The
          existing SummaryCards is the Expenses-view headline (You're
          owed / You owe / Outstanding). On the Payments tab the
          PaymentCreditsPanel renders its own metrics so we hide
          SummaryCards there to keep one source of truth above the
          fold. */}
      {activeTab === "expenses" && (
        <SummaryCards
          summary={summary}
          currency={currency}
          userId={user?.id}
          outstandingCount={outstandingCount}
          onSettleAll={() => setSettleOpen(true)}
        />
      )}

      {/* v77.99.102 (iter 122.exp1) — Export PDF panel collapsed by
          default so the page-view focus stays on the headline +
          summary. Users with a regular need to download monthly
          reports can expand it once and we'll persist that in a
          future iter. */}
      {activeTab === "expenses" && (
        <details
          className="border border-[#D9E1E3] bg-white rounded-md"
          data-testid="export-pdf-panel"
          onToggle={(e) => {
            // Iter 122.trial-pivot — Opening the export panel is a
            // strong "I'm about to download something" signal. Morph
            // the topbar TrialPill to the upgrade affordance so trial
            // users see the path before they click a button that
            // 402s.
            if (e.currentTarget.open) {
              import("@/lib/exportIntent").then(({ broadcastExportIntent }) =>
                broadcastExportIntent({ feature: "Expenses PDF", ttl_ms: 12000 })
              );
            }
          }}
        >
          <summary className="px-4 py-2.5 cursor-pointer text-xs uppercase tracking-[0.1em] text-[#6F828A] inline-flex items-center gap-1.5 hover:bg-[#F4FAFA] rounded-md transition-colors list-none [&::-webkit-details-marker]:hidden">
            <Download className="w-3 h-3" strokeWidth={2} /> Export PDF
            <span className="text-[10px] normal-case tracking-normal text-[#6F828A]/70 ml-2">Monthly report or snapshot</span>
          </summary>
          <div className="px-4 pb-3 pt-1 flex flex-wrap items-center gap-2 border-t border-[#D9E1E3]">
            <span className="text-[10px] text-[#6F828A]/70 w-full">Downloads use the month selected here, not the date range above.</span>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-[160px] h-9 border-[#D9E1E3] bg-white rounded-sm"
              data-testid="expense-month-input"
            />
            {/* v77.99.159 (iter 137.expenses-standard-vs-detailed) —
                Layout selector. STANDARD = concise financial statement
                (no embedded receipts). DETAILED = full vouched record
                with every supporting receipt embedded alongside its
                expense (legacy `docket` layout preserved as the
                underlying value for backwards compatibility — the
                server accepts both `detailed` and `docket`). */}
            <div
              className="inline-flex items-center gap-1 h-9 bg-[#F4FAFA] border border-[#D9E1E3] rounded-sm p-0.5"
              role="radiogroup"
              aria-label="Report type"
              data-testid="expense-layout-toggle"
            >
              <button
                type="button"
                role="radio"
                aria-checked={exportLayout === "standard"}
                onClick={() => setExportLayout("standard")}
                className={`px-2.5 py-1 text-xs rounded-[3px] transition-colors ${
                  exportLayout === "standard"
                    ? "bg-white text-[#0B2E32] shadow-sm font-medium"
                    : "text-[#6F828A] hover:text-[#0B2E32]"
                }`}
                data-testid="expense-layout-standard"
                title="Standard — concise financial statement (no embedded receipts)"
              >
                Standard
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={exportLayout === "docket"}
                onClick={() => setExportLayout("docket")}
                className={`px-2.5 py-1 text-xs rounded-[3px] transition-colors ${
                  exportLayout === "docket"
                    ? "bg-white text-[#0B2E32] shadow-sm font-medium"
                    : "text-[#6F828A] hover:text-[#0B2E32]"
                }`}
                data-testid="expense-layout-detailed"
                title="Detailed — full vouched record with every supporting receipt embedded"
              >
                Detailed
              </button>
            </div>
            <MediatorPdfDownloadButton
              exportUrl={`${API}/expenses/export/pdf?month=${month}${exportLayout === "docket" ? "&layout=detailed" : ""}`}
              label={exportLayout === "docket" ? "Download detailed report" : "Download report"}
              testid="expenses-mediator-pdf"
            />
            {!isMediator && (
              <Button
                variant="outline"
                onClick={exportMonthly}
                className="h-9 border-[#D9E1E3] bg-white rounded-sm"
                data-testid="expense-export-pdf"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportLayout === "docket" ? "Download detailed report" : "Download report"}
              </Button>
            )}
            {!isMediator && <SnapshotExportMenu childFilter={childFilter} />}
          </div>
          {/* v77.99.159 (iter 137.expenses-standard-vs-detailed) —
              Dynamic subtitle under the toggle; compact enough to
              remain mobile-friendly. */}
          <div
            className="text-[11px] text-[#6F828A] mt-1 leading-snug"
            data-testid="expense-layout-hint"
          >
            {exportLayout === "docket"
              ? "Full expense record including supporting receipts and documents."
              : "Summary expense report for everyday record keeping."}
          </div>
        </details>
      )}
      {/* v77.99.86 — Upcoming Expenses moved out of /app/expenses into
          its own dedicated planning page. What's left here is a
          compact summary card linking to that page. The same component
          (variant="summary") still hosts the create dialog, so the
          top-bar "Flag upcoming expense" button on this page can open
          it inline via the forwarded ref. */}
      {/* Iter 122.exp-segmented — UpcomingExpensesSection only makes
          sense on the Expenses tab — it's a forward-looking expense
          planner, not a payment-history view. */}
      {activeTab === "expenses" && (
        <UpcomingExpensesSection
          ref={upcomingRef}
          variant="summary"
          currency={currency}
          childList={children}
          isMediator={isMediator}
        />
      )}

      {/* v77.99.104 — Payments tab. Renders only when activeTab === "payments".
          The expenses content below is wrapped in the inverse so the two tabs
          never overlap. */}
      {activeTab === "payments" && (
        <PaymentCreditsPanel
          currency={currency}
          childList={children}
          coparents={[]}
          onCreditsChanged={() => load()}
          pendingAllocationTarget={pendingAllocationTarget}
          onPendingHandled={() => setPendingAllocationTarget(null)}
        />
      )}
      {activeTab === "expenses" && summary && (
        <div className="space-y-4" data-testid="expenses-summary-rest">
          {/* v77.3 — Per-child sub-totals (All-children mode only).
              Renders one compact card per child showing that child's
              independent co-parent pairing: total logged, outstanding,
              the linked parents, and the net settlement transfer(s).
              Hidden when child-scoped (the page already shows the
              specific child's view) or when there's only one child
              with activity (would just duplicate the headline). */}
          <PerChildSummary
            summary={summary}
            currency={currency}
            userId={user?.id}
            selectedChildId={selectedChildId}
            onSelectChild={setSelectedChildId}
          />

          {/* v77.99.61 — Per-parent breakdown collapsed by default.
              The Net settlement hero + the three top tiles already
              answer "who owes whom right now" for most users. Mediators
              and detail-oriented users can expand the breakdown when
              they need it. Preference persists per user via localStorage. */}
          <PerMemberBreakdown
            summary={summary}
            members={members}
            childList={children}
            user={user}
            currency={currency}
            childFilter={childFilter}
          />
        </div>
      )}

      {/* Expense list — rich cards */}
      {/* v77.56 — Hide the per-child filter chip bar whenever the
          global ChildScope is already locked to a single child. The
          page is already child-specific in that mode (see `childFilter
          = selectedChildId || "all"` above), so showing a "Filter by
          child" picker here is redundant and confusing — tapping a
          chip would silently swap which child the page is for instead
          of doing what the user expects (filter within the current
          view). The picker only makes sense in "All children" mode. */}
      {/* v77.99.115d — The child-filter bar AND the category/status
          filter bar below it BOTH apply only to the expense list, but
          historically they rendered on every tab. On the "Payments
          received" tab the user saw "0 expenses" beneath the credit
          rows — confusing UX. Hide both filter rows when we're not on
          the expenses tab. The list section already gates on
          activeTab === "expenses" further down. */}
      {activeTab === "expenses" && children.length > 0 && !selectedChildId && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="child-filter-bar">
          <span className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Filter by child:</span>
          <button type="button" onClick={() => setChildFilter("all")}
            className={`px-2.5 py-1 border rounded-sm text-xs transition-colors ${childFilter === "all" ? "bg-[#1F9FA4] border-[#1F9FA4] text-white" : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"}`}
            data-testid="child-filter-all">All</button>
          {children.map((c) => {
            const active = childFilter === c.id;
            return (
              <span key={c.id} className="inline-flex items-center gap-0.5 rounded-sm overflow-hidden border border-[#D9E1E3]">
                <button type="button" onClick={() => setChildFilter(c.id)}
                  className={`px-2.5 py-1 text-xs transition-colors inline-flex items-center gap-1.5 ${active ? "bg-[#2E3A3F] text-white" : "bg-white text-[#6F828A] hover:bg-[#EEF3F4]"}`}
                  data-testid={`child-filter-${c.id}`}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }}></span>
                  {c.name}
                </button>
                <Link to={`/app/children/${c.id}/expenses`}
                  className={`px-1.5 py-1 text-[10px] transition-colors border-l border-[#D9E1E3] ${active ? "bg-[#2E3A3F] text-white hover:bg-[#435358]" : "bg-white text-[#1F9FA4] hover:bg-[#E0F2F2]"}`}
                  title={`Open ${c.name}'s dedicated expense page`}
                  aria-label={`Open ${c.name}'s expense page`}
                  data-testid={`child-open-page-${c.id}`}>
                  →
                </Link>
              </span>
            );
          })}
        </div>
      )}

      {/* v77.89 — Category + "no receipts attached" filters. Sit
          directly above the expense list so users can narrow down
          quickly. URL-state-driven so a chosen filter survives a
          refresh and is shareable / deep-linkable.
          v77.99.115d — Only render on the Expenses tab; the filter
          targets the expense list and was confusing under "Payments
          received". */}
      {activeTab === "expenses" && (() => {
        // v77.99.106 (iter 122.m3) — Consolidated filter bar.
        // The compact pill summarises active filters in plain English
        // and shows the result count. Tapping it opens a bottom sheet
        // that holds every filter control. This collapses what was a
        // 2-row inline filter strip into a single thumb-friendly
        // surface on mobile while keeping every filter available.
        const activeCount =
          (categoryFilter && categoryFilter !== "all" ? 1 : 0) +
          (statusFilter && statusFilter !== "all" ? 1 : 0) +
          (noReceiptsOnly ? 1 : 0);
        const labelBits = [];
        if (categoryFilter && categoryFilter !== "all") labelBits.push(catLabel(categoryFilter));
        if (statusFilter && statusFilter !== "all") {
          const stat = { outstanding: "Outstanding", partial: "Part-paid", paid: "Settled", voided: "Voided" }[statusFilter] || statusFilter;
          labelBits.push(stat);
        }
        if (noReceiptsOnly) labelBits.push("No receipts");
        const summary = labelBits.length ? labelBits.join(" · ") : "All expenses";
        return (
        <div className="flex items-center gap-2 flex-wrap pt-1" data-testid="expense-filter-row">
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-2 h-9 px-3 rounded-sm text-xs border transition-colors ${
                  activeCount > 0
                    ? "bg-[#D6F0EF] border-[#A7D8DA] text-[#0F5658]"
                    : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F4FAFA]"
                }`}
                data-testid="expense-filter-trigger"
                aria-label="Open filters"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.8} />
                <span className="font-medium">{summary}</span>
                {activeCount > 0 ? (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#1F9FA4] text-white text-[10px] font-semibold">
                    {activeCount}
                  </span>
                ) : null}
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-white">
              <SheetHeader>
                <SheetTitle className="font-heading text-lg text-[#2E3A3F]">Filter expenses</SheetTitle>
              </SheetHeader>
              <div className="space-y-5 py-4">
                <div>
                  <label className="text-xs text-[#6F828A] mb-1.5 block">Category</label>
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="h-9 w-full text-sm" data-testid="expense-filter-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      {CATS.map((c) => (
                        <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[#6F828A] mb-1.5 block">Status</label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9 w-full text-sm" data-testid="expense-filter-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="expense-filter-status-all">All statuses</SelectItem>
                      <SelectItem value="outstanding" data-testid="expense-filter-status-outstanding">Outstanding</SelectItem>
                      <SelectItem value="partial" data-testid="expense-filter-status-partial">Part-paid</SelectItem>
                      <SelectItem value="paid" data-testid="expense-filter-status-paid">Settled</SelectItem>
                      <SelectItem value="voided" data-testid="expense-filter-status-voided">Voided</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[#6F828A] mb-1.5 block">Receipts</label>
                  <button
                    type="button"
                    onClick={toggleNoReceipts}
                    className={`w-full inline-flex items-center justify-between px-3 h-9 border rounded-sm text-sm transition-colors ${
                      noReceiptsOnly
                        ? "bg-[#FBF0DC] border-[#E2C588] text-[#6F4E1E]"
                        : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F4FAFA]"
                    }`}
                    data-testid="expense-filter-no-receipts"
                    aria-pressed={noReceiptsOnly}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Paperclip className="w-3.5 h-3.5" /> Show only expenses missing receipts
                    </span>
                    {noReceiptsOnly ? <Check className="w-4 h-4" /> : null}
                  </button>
                </div>
              </div>
              <SheetFooter className="flex-row gap-2 pt-2 border-t border-[#D9E1E3]">
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="flex-1 h-10 rounded-sm border border-[#D9E1E3] text-sm text-[#6F828A] hover:bg-[#F4FAFA] disabled:opacity-50"
                  disabled={activeCount === 0}
                  data-testid="expense-filter-clear-all"
                >
                  Clear filters
                </button>
                <button
                  type="button"
                  className="flex-1 h-10 rounded-sm bg-[#1F9FA4] hover:bg-[#178085] text-white text-sm font-medium"
                  onClick={(e) => {
                    // Close the sheet by dispatching a click on the
                    // overlay via the radix close trigger pattern.
                    const sheet = e.currentTarget.closest("[data-state]");
                    if (sheet) {
                      const closeBtn = sheet.querySelector("[data-testid=\"sheet-close\"], button[aria-label='Close']");
                      if (closeBtn) closeBtn.click();
                    }
                  }}
                  data-testid="expense-filter-apply"
                >
                  Show {visibleItems.length} {visibleItems.length === 1 ? "expense" : "expenses"}
                </button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
          <span
            className="ml-auto text-[11px] text-[#6F828A] font-mono-data"
            data-testid="expense-filter-count"
          >
            {visibleItems.length} {visibleItems.length === 1 ? "expense" : "expenses"}
          </span>
        </div>
        );
      })()}

      {activeTab === "expenses" && (
      <div className="space-y-3" data-testid="expenses-list">
        {missingOnly && (
          <div
            className="border border-[#E6CFA2] bg-[#FDF4E2] rounded-md p-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="missing-receipt-filter-banner"
          >
            <div className="text-sm text-[#5C3A1E] inline-flex items-center gap-2">
              <Paperclip className="w-3.5 h-3.5" />
              Showing only your expenses missing a receipt
              <span className="text-[#8F6B1F]">· {visibleItems.length}</span>
            </div>
            <button
              type="button"
              onClick={clearMissingFilter}
              className="text-[10px] uppercase tracking-[0.1em] text-[#8F6B1F] hover:text-[#5C3A1E] underline-offset-2 hover:underline"
              data-testid="missing-receipt-filter-clear"
            >
              Clear filter
            </button>
          </div>
        )}
        {visibleItems.length === 0 && (
          <div className="border border-[#D9E1E3] bg-white rounded-md p-10 text-center text-[#6F828A]" data-testid={loading ? "expenses-loading" : "expenses-empty"}>
            <WalletIcon className="w-6 h-6 mx-auto mb-2" strokeWidth={1.5} />
            {loading ? (
              "Loading expenses…"
            ) : missingOnly ? (
              "All your expenses have receipts attached. Nice work."
            ) : period !== "all" || activeRange.from || activeRange.to ? (
              <>
                No expenses in the selected period.
                <button
                  type="button"
                  onClick={() => { setPeriod("all"); setCustomRange({ from: "", to: "" }); }}
                  className="ml-2 underline text-[#1F9FA4] hover:text-[#0F5658]"
                  data-testid="expenses-empty-show-all"
                >
                  Show all
                </button>
              </>
            ) : (
              "No expenses yet."
            )}
          </div>
        )}
        {(() => {
          // v77.99.102 (iter 122.exp1) — Group the list by status so
          // the user's eye lands on actionable rows first. Order:
          //   1. Outstanding / partial / disputed  (most urgent)
          //   2. Settled / paid
          //   3. Voided (collapsed by default)
          // When there are zero outstanding rows we surface a calm
          // "Everything is settled." line in place of the group header.
          if (visibleItems.length === 0) return null;
          const outstanding = [];
          const settled = [];
          const voided = [];
          for (const x of visibleItems) {
            if (x.voided) { voided.push(x); continue; }
            const st = x.status || x.breakdown?.status || "outstanding";
            if (st === "paid" || st === "settled") settled.push(x);
            else outstanding.push(x);
          }
          return (
            <>
              <div
                className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium pt-2 pb-1"
                data-testid="expenses-group-outstanding-label"
              >
                {outstanding.length > 0
                  ? `Outstanding · ${outstanding.length}`
                  : "Outstanding"}
              </div>
              {outstanding.length === 0 ? (
                <div
                  className="border border-[#A7D8DA] bg-[#D6F0EF] text-[#0F5658] rounded-md p-4 text-sm flex items-center gap-2.5"
                  data-testid="expenses-everything-settled"
                >
                  <span
                    className="h-2 w-2 rounded-full bg-[#1F9FA4] flex-shrink-0"
                    aria-hidden="true"
                  />
                  {period !== "all" || activeRange.from || activeRange.to
                    ? "Everything in the selected period is settled."
                    : "Everything is settled."}
                </div>
              ) : (
                outstanding.map((x) => (
                  <ExpenseRow
                    key={x.id}
                    x={x}
                    isExpanded={!!expanded[x.id]}
                    ctx={rowCtx}
                    actions={rowActions}
                  />
                ))
              )}
              {settled.length > 0 && (
                <div
                  className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium pt-4 pb-1"
                  data-testid="expenses-group-settled-label"
                >
                  Settled · {settled.length}
                </div>
              )}
              {settled.map((x) => (
                <ExpenseRow
                  key={x.id}
                  x={x}
                  isExpanded={!!expanded[x.id]}
                  ctx={rowCtx}
                  actions={rowActions}
                />
              ))}
              {voided.length > 0 && (
                <details
                  className="mt-4 border border-[#D9E1E3] bg-white rounded-md"
                  data-testid="expenses-voided-collapsible"
                >
                  <summary className="px-4 py-2.5 cursor-pointer text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium hover:bg-[#F4FAFA] rounded-md transition-colors list-none [&::-webkit-details-marker]:hidden inline-flex items-center gap-1.5 w-full">
                    Voided · {voided.length}
                    <span className="text-[10px] normal-case tracking-normal text-[#6F828A]/70 ml-2">
                      hidden by default · tap to view
                    </span>
                  </summary>
                  <div className="px-2 pb-2 space-y-2 border-t border-[#D9E1E3] pt-2">
                    {voided.map((x) => (
                      <ExpenseRow
                        key={x.id}
                        x={x}
                        isExpanded={!!expanded[x.id]}
                        ctx={rowCtx}
                        actions={rowActions}
                      />
                    ))}
                  </div>
                </details>
              )}
            </>
          );
        })()}
      </div>
      )}

      <ExpenseDialogs
        currency={currency}
        childList={children}
        items={items}
        CATS={CATS}
        catLabel={catLabel}
        SPLIT_OPTIONS_NON_DEFAULT={SPLIT_OPTIONS_NON_DEFAULT}
        PM_OPTIONS={PM_OPTIONS}
        PM_LABEL={PM_LABEL}
        eligibleParentsFor={eligibleParentsFor}
        nameOf={nameOf}
        settleOpen={settleOpen} setSettleOpen={setSettleOpen}
        outstandingCount={outstandingCount} summary={summary}
        paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
        paymentRef={paymentRef} setPaymentRef={setPaymentRef}
        settleBusy={settleBusy} settleAll={settleAll}
        payFor={payFor} setPayFor={setPayFor}
        payForm={payForm} setPayForm={setPayForm} submitPay={submitPay}
        confirmFor={confirmFor} setConfirmFor={setConfirmFor}
        disputeReason={disputeReason} setDisputeReason={setDisputeReason}
        confirmBusy={confirmBusy} doDispute={doDispute} doConfirm={doConfirm}
        receiptFor={receiptFor} setReceiptFor={setReceiptFor}
        receiptForm={receiptForm} setReceiptForm={setReceiptForm}
        receiptBusy={receiptBusy} submitReceipt={submitReceipt}
        attachFor={attachFor} setAttachFor={setAttachFor}
        attachFile={attachFile} setAttachFile={setAttachFile}
        attachBusy={attachBusy} submitAttach={submitAttach}
        editFor={editFor} setEditFor={setEditFor}
        editForm={editForm} setEditForm={setEditForm}
        editBusy={editBusy} submitEdit={submitEdit}
        openAttach={openAttach} setNotRequired={setNotRequired} detachReceipt={detachReceipt}
        voidFor={voidFor} setVoidFor={setVoidFor}
        voidReason={voidReason} setVoidReason={setVoidReason}
        submitVoid={submitVoid}
      />


      {settlements.length > 0 && (
        <div className="border border-[#D9E1E3] bg-white rounded-md p-5" data-testid="settlements-section">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-medium">Past settlements</h2>
            <span className="text-xs text-[#6F828A]">{settlements.length} record{settlements.length === 1 ? "" : "s"}</span>
          </div>
          <ul className="mt-3 divide-y divide-[#D9E1E3]">
            {settlements.map((s) => (
              <li key={s.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`settlement-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#2E3A3F]">
                    {s.transfers && s.transfers.length > 1 ? (
                      <ul className="space-y-0.5">
                        {s.transfers.map((tx, i) => (
                          <li key={`${s.id}-${i}`}>
                            <span className="font-semibold">{tx.from_name}</span> → <span className="font-semibold">{tx.to_name}</span> · <span className="font-mono-data">{money(tx.amount, currency)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : s.amount > 0 ? (
                      <><span className="font-semibold">{s.from_name || s.transfers?.[0]?.from_name}</span> → <span className="font-semibold">{s.to_name || s.transfers?.[0]?.to_name}</span> · <span className="font-mono-data">{money(s.amount, currency)}</span></>
                    ) : (
                      <span className="font-semibold">Balances were even</span>
                    )}
                  </div>
                  <div className="text-xs text-[#6F828A] font-mono-data">
                    {formatDate(s.created_at)} {s.created_at?.slice(11, 16)} · {s.payment_method_label || s.payment_method} · {s.expense_count} expense{s.expense_count === 1 ? "" : "s"}
                    {s.payment_reference && <> · ref {s.payment_reference}</>}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="border-[#D9E1E3] rounded-sm" onClick={() => downloadFile(`${API}/settlements/${s.id}/pdf`, `settlement-${s.id.slice(0,8)}.pdf`, { label: "Generating settlement PDF", successMessage: "PDF ready" })} data-testid={`settlement-pdf-${s.id}`}>
                  <Download className="w-3.5 h-3.5 mr-2" /> Receipt
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      </div>
      {/* v77.99.106 (iter 122.m3) — Sticky "Add expense" FAB on mobile.
          One-handed primary action. Hidden on sm+ where the top-bar
          "Add expense" button is already easy to reach. Hidden for the
          mediator role since they cannot add expenses. Iter 122.exp-
          segmented — also hidden when the user is viewing the
          Payments tab; PaymentCreditsPanel has its own primary CTA. */}
      {!isMediator && activeTab === "expenses" && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="sm:hidden fixed bottom-20 right-4 z-30 h-12 w-12 rounded-full bg-[#1F9FA4] hover:bg-[#178085] text-white shadow-md flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1F9FA4]/55 transition-colors"
          aria-label="Add expense"
          data-testid="add-expense-fab"
        >
          <Plus className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

/**
 * ChildAssignPicker has been extracted to
 * @/components/expenses/ChildAssignPicker.jsx
 * (v77.99.19 — Phase 3 frontend decomposition slice 1).
 */

